/**
 * Lightweight global toast system — zero dependency.
 *
 * A module-level store (subscribe / getSnapshot) drives a single <Toaster/>
 * mounted once in the shell. `useToast()` returns imperative helpers that work
 * from any component without a wrapping context provider. Toasts auto-dismiss.
 *
 *   const toast = useToast();
 *   toast.success('Contact créé');
 *   toast.error('Échec de la sauvegarde');
 */

import { useMemo, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';

export type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
}

const DEFAULT_DURATION = 4000;

let toasts: Toast[] = [];
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): Toast[] {
  return toasts;
}

function dismiss(id: string): void {
  const next = toasts.filter((t) => t.id !== id);
  if (next.length !== toasts.length) {
    toasts = next;
    emit();
  }
}

function push(type: ToastType, message: string, duration = DEFAULT_DURATION): string {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  toasts = [...toasts, { id, type, message }];
  emit();
  if (duration > 0 && typeof window !== 'undefined') {
    window.setTimeout(() => dismiss(id), duration);
  }
  return id;
}

/** Imperative toast helpers, stable across renders. */
export function useToast() {
  return useMemo(
    () => ({
      success: (message: string, duration?: number) => push('success', message, duration),
      error: (message: string, duration?: number) => push('error', message, duration),
      info: (message: string, duration?: number) => push('info', message, duration),
      dismiss,
    }),
    [],
  );
}

const TYPE_STYLES: Record<ToastType, { accent: string; icon: string }> = {
  success: { accent: 'border-l-success', icon: '✓' },
  error: { accent: 'border-l-danger', icon: '✕' },
  info: { accent: 'border-l-primary', icon: 'i' },
};

const TYPE_LABEL: Record<ToastType, string> = {
  success: 'Succès',
  error: 'Erreur',
  info: 'Information',
};

/** Renders the active toasts. Mount exactly once (in the shell). */
export function Toaster() {
  const items = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="pointer-events-none fixed bottom-4 left-4 z-[10001] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2"
      role="region"
      aria-label="Notifications"
    >
      {items.map((toast) => {
        const style = TYPE_STYLES[toast.type];
        return (
          <div
            key={toast.id}
            role={toast.type === 'error' ? 'alert' : 'status'}
            className={`pointer-events-auto flex items-start gap-2 rounded-token border border-l-4 border-border bg-surface px-3 py-2 text-sm text-foreground shadow-elevation-2 ${style.accent}`}
          >
            <span aria-hidden="true" className="mt-0.5 font-bold text-muted">
              {style.icon}
            </span>
            <span className="min-w-0 flex-1 break-words">
              <span className="sr-only">{TYPE_LABEL[toast.type]} : </span>
              {toast.message}
            </span>
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              aria-label="Fermer la notification"
              className="shrink-0 rounded text-muted hover:text-foreground"
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
        );
      })}
    </div>,
    document.body,
  );
}
